import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ArrowRight, Building2, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import SlotifyLogo from "@/components/SlotifyLogo";
import { useLanguage } from "@/contexts/LanguageContext";

const dayKeys = ["onboarding.mon", "onboarding.tue", "onboarding.wed", "onboarding.thu", "onboarding.fri", "onboarding.sat", "onboarding.sun"] as const;

const Onboarding = () => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const { user, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Org details
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [cocNumber, setCocNumber] = useState("");
  const [vatNumber, setVatNumber] = useState("");

  // Opening hours
  const [hours, setHours] = useState(
    dayKeys.map((_, i) => ({
      open: i < 5,
      from: "09:00",
      to: "17:00",
    }))
  );

  const toggleDay = (index: number, open: boolean) => {
    setHours((prev) => prev.map((h, i) => (i === index ? { ...h, open } : h)));
  };

  const updateTime = (index: number, field: "from" | "to", value: string) => {
    setHours((prev) => prev.map((h, i) => (i === index ? { ...h, [field]: value } : h)));
  };

  const handleFinish = async () => {
    if (!companyName.trim()) {
      toast.error(t("onboarding.name_required"));
      return;
    }
    setLoading(true);
    try {
      const openingHours = hours.map((h, i) => ({
        day_of_week: i,
        is_open: h.open,
        open_time: h.from,
        close_time: h.to,
      }));

      const { error } = await supabase.rpc("create_organisation_for_user", {
        _name: companyName,
        _email: email || null,
        _phone: phone || null,
        _address: address || null,
        _postal_code: postalCode || null,
        _city: city || null,
        _country: country || null,
        _coc_number: cocNumber || null,
        _vat_number: vatNumber || null,
        _opening_hours: openingHours,
      });

      if (error) throw error;

      await refreshProfile();
      toast.success(t("onboarding.success"));
      navigate("/dashboard");
    } catch (err: any) {
      toast.error(err.message || t("onboarding.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <SlotifyLogo className="h-9" />
        </div>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-8 justify-center">
          {[0, 1].map((s) => (
            <div
              key={s}
              className={`h-2 rounded-full transition-all ${
                s <= step ? "bg-primary w-16" : "bg-muted w-8"
              }`}
            />
          ))}
        </div>

        {step === 0 && (
          <section className="rounded-xl border border-border bg-card p-6 shadow-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground">{t("onboarding.org_title")}</h1>
                <p className="text-sm text-muted-foreground">{t("onboarding.org_desc")}</p>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2 md:col-span-2">
                <Label>{t("onboarding.company_name")}</Label>
                <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder={t("onboarding.company_placeholder")} required />
              </div>
              <div className="space-y-2">
                <Label>{t("onboarding.email")}</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="info@company.com" />
              </div>
              <div className="space-y-2">
                <Label>{t("onboarding.phone")}</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+31 20 123 4567" />
              </div>
              <div className="space-y-2">
                <Label>{t("onboarding.address")}</Label>
                <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder={t("onboarding.address")} />
              </div>
              <div className="space-y-2">
                <Label>{t("onboarding.postal_code")}</Label>
                <Input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} placeholder="1234 AB" />
              </div>
              <div className="space-y-2">
                <Label>{t("onboarding.city")}</Label>
                <Input value={city} onChange={(e) => setCity(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>{t("onboarding.country")}</Label>
                <Input value={country} onChange={(e) => setCountry(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>{t("onboarding.coc")}</Label>
                <Input value={cocNumber} onChange={(e) => setCocNumber(e.target.value)} placeholder="12345678" />
              </div>
              <div className="space-y-2">
                <Label>{t("onboarding.vat")}</Label>
                <Input value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} placeholder="NL123456789B01" />
              </div>
            </div>
            <Button className="mt-6 w-full" onClick={() => setStep(1)} disabled={!companyName.trim()}>
              {t("onboarding.continue")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </section>
        )}

        {step === 1 && (
          <section className="rounded-xl border border-border bg-card p-6 shadow-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Clock className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground">{t("onboarding.hours_title")}</h1>
                <p className="text-sm text-muted-foreground">{t("onboarding.hours_desc")}</p>
              </div>
            </div>
            <div className="space-y-3">
              {hours.map((h, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-28">
                    <span className="text-sm font-medium text-foreground">{t(dayKeys[i])}</span>
                  </div>
                  <Switch checked={h.open} onCheckedChange={(v) => toggleDay(i, v)} />
                  {h.open ? (
                    <div className="flex items-center gap-2">
                      <Input className="w-24" type="time" value={h.from} onChange={(e) => updateTime(i, "from", e.target.value)} />
                      <span className="text-sm text-muted-foreground">{t("onboarding.to")}</span>
                      <Input className="w-24" type="time" value={h.to} onChange={(e) => updateTime(i, "to", e.target.value)} />
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">{t("onboarding.closed")}</span>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setStep(0)}>{t("onboarding.back")}</Button>
              <Button className="flex-1" onClick={handleFinish} disabled={loading}>
                {loading ? t("onboarding.finishing") : t("onboarding.finish")}
                {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
